import { getTodayIsoDateString, toIsoDateString } from '@@utils/dates';

export type CertificationStatus = 'expired' | 'expiring' | 'valid';

export const CERTIFICATION_STATUS_EXPIRED: CertificationStatus = 'expired';

export const CERTIFICATION_STATUS_EXPIRING: CertificationStatus = 'expiring';

export const CERTIFICATION_STATUS_VALID: CertificationStatus = 'valid';

// A certification reads as expiring once it is this close to its expiration date,
// which is also the window the members list filter offers and the number its
// label is built from.
export const EXPIRING_SOON_DAY_COUNT = 60;

const MILLISECONDS_PER_DAY = 86400000;

const START_OF_DAY_SUFFIX = 'T00:00:00.000Z';

const EXPIRED_DAY_COUNT_CEILING = 0;

// Both sides are pinned to UTC midnight of their calendar day, so the count is a
// whole number of days and does not drift with the viewer's time zone.
export const getDaysUntilExpiration = (
    expirationDate?: Date | string | null
): number | null => {
    if (!expirationDate) {
        return null;
    }

    const expirationTime = Date.parse(`${toIsoDateString(expirationDate)}${START_OF_DAY_SUFFIX}`);
    const todayTime = Date.parse(`${getTodayIsoDateString()}${START_OF_DAY_SUFFIX}`);

    return Math.round((expirationTime - todayTime) / MILLISECONDS_PER_DAY);
};

// A record the backend holds no expiration date for cannot be shown as lapsing, so
// it reads as valid rather than inventing an expiry the admin never entered.
export const getCertificationStatus = (
    expirationDate?: Date | string | null
): CertificationStatus => {
    const daysUntilExpiration = getDaysUntilExpiration(expirationDate);

    if (daysUntilExpiration === null) {
        return CERTIFICATION_STATUS_VALID;
    }

    if (daysUntilExpiration < EXPIRED_DAY_COUNT_CEILING) {
        return CERTIFICATION_STATUS_EXPIRED;
    }

    return daysUntilExpiration <= EXPIRING_SOON_DAY_COUNT
        ? CERTIFICATION_STATUS_EXPIRING
        : CERTIFICATION_STATUS_VALID;
};

// certificationCategories is one free-text field, so splitting it is a front-end
// decision: the comma is the delimiter, each part is trimmed and blank parts are
// dropped. Text that holds no comma stays whole and reads as a single category.
export const CERTIFICATION_CATEGORY_SEPARATOR = ',';

export const splitCertificationCategories = (categories?: string | null): string[] =>
    (categories ?? '')
        .split(CERTIFICATION_CATEGORY_SEPARATOR)
        .map(category => category.trim())
        .filter(Boolean);
